import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import {
  AssistantMessageContent,
  ChartData,
  MessageType,
} from "duck/ui/chat/types";
import { useTranslation } from "react-i18next";
import { VegaEmbed, type useVegaEmbed } from "react-vega";
import { expressionInterpreter } from "vega-interpreter";
import { compile, TopLevelSpec } from "vega-lite";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";

import { useConfigContext } from "shared/contexts/ConfigContext";

import IconButton from "features/components/IconButton";
import Menu from "features/components/Menu";

import { Renderer, RendererContext } from "./types";

type Result = NonNullable<ReturnType<typeof useVegaEmbed>>;

const font = "IBM Plex Sans";
const VEGA_EDITOR_URL = "https://vega.github.io/editor/";

interface ChartActions {
  export: boolean;
  source: boolean;
  compiled: boolean;
  editor: boolean;
}

const downloadUrl = (url: string, filename: string) => {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
};

const openSource = (source: string, title: string) => {
  const win = window.open("");
  if (!win) return;

  win.document.title = title;
  const pre = win.document.createElement("pre");
  const code = win.document.createElement("code");
  code.className = "json";
  // textContent (not innerHTML): the spec is server/AI-controlled and must
  // never be parsed as HTML, otherwise a crafted string field breaks out.
  code.textContent = source;
  pre.appendChild(code);
  win.document.body.appendChild(pre);
};

const openInEditor = (spec: string) => {
  const editor = window.open(VEGA_EDITOR_URL)!;
  const { origin } = new URL(VEGA_EDITOR_URL);
  const data = { spec, mode: "vega-lite" };

  let count = 40;

  const listen = (evt: MessageEvent) => {
    if (evt.source === editor) {
      count = 0;
      window.removeEventListener("message", listen);
    }
  };
  window.addEventListener("message", listen);

  const send = () => {
    if (count <= 0) return;

    editor.postMessage(data, origin);
    setTimeout(send, 250);
    count -= 1;
  };
  setTimeout(send, 250);
};

interface ActionToolbarProps {
  result: Result;
  actions: ChartActions;
}

const ActionToolbar = ({ result, actions }: ActionToolbarProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const handleExportPng = useCallback(async () => {
    const url = await result.view.toImageURL("png");
    downloadUrl(url, "chart.png");
  }, [result]);

  const handleExportSvg = useCallback(async () => {
    const url = await result.view.toImageURL("svg");
    downloadUrl(url, "chart.svg");
  }, [result]);

  const handleSource = useCallback(() => {
    openSource(
      JSON.stringify(result.spec, null, 2),
      t("duck:chart_vega_lite_json_source")
    );
  }, [result, t]);

  const handleCompiled = useCallback(() => {
    openSource(
      JSON.stringify(result.vgSpec, null, 2),
      t("duck:chart_vega_json_source")
    );
  }, [result, t]);

  const handleEditor = useCallback(() => {
    openInEditor(JSON.stringify(result.spec, null, 2));
  }, [result]);

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const runAction = (action: () => void | Promise<void>) => {
    handleCloseMenu();
    void action();
  };

  const menuOptions = [
    actions.export
      ? {
          key: "export-png",
          testId: "duck-chart-save-png",
          label: t("duck:chart_save_as_png"),
          icon: <ImageOutlinedIcon />,
          onClick: () => runAction(handleExportPng),
        }
      : null,
    actions.export
      ? {
          key: "export-svg",
          testId: "duck-chart-save-svg",
          label: t("duck:chart_save_as_svg"),
          icon: <InsertDriveFileOutlinedIcon />,
          onClick: () => runAction(handleExportSvg),
        }
      : null,
    actions.source
      ? {
          key: "source",
          label: t("duck:chart_view_vega_lite_source"),
          icon: <CodeOutlinedIcon />,
          onClick: () => runAction(handleSource),
        }
      : null,
    actions.compiled
      ? {
          key: "compiled",
          label: t("duck:chart_view_compiled_vega"),
          icon: <AccountTreeOutlinedIcon />,
          onClick: () => runAction(handleCompiled),
        }
      : null,
    actions.editor
      ? {
          key: "editor",
          label: t("duck:chart_open_in_vega_editor"),
          icon: <OpenInNewOutlinedIcon />,
          onClick: () => runAction(handleEditor),
        }
      : null,
  ].filter(Boolean);

  return (
    <>
      <IconButton
        size="small"
        tooltip={t("duck:chart_actions")}
        onClick={handleOpenMenu}
        data-testid="chart-actions-menu-trigger"
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {menuOptions
          .filter((option) => option !== null)
          .map((option) => (
            <MenuItem
              key={option.key}
              onClick={option.onClick}
              data-testid={option.testId || ""}
            >
              <ListItemIcon>{option.icon}</ListItemIcon>
              <ListItemText>{option.label}</ListItemText>
            </MenuItem>
          ))}
      </Menu>
    </>
  );
};

interface Props {
  chart: TopLevelSpec;
}

const VegaChartContainer = ({ chart }: Props) => {
  const { userInfo } = useConfigContext();
  const [vegaResult, setVegaResult] = useState<Result | null>(null);

  const actions: ChartActions = {
    export: true,
    source: userInfo.isSuperAdmin,
    compiled: userInfo.isSuperAdmin,
    editor: userInfo.isSuperAdmin,
  };

  return (
    <div className="overflow-auto relative">
      {vegaResult && (
        <div className="absolute right-0 top-0 z-10">
          <ActionToolbar result={vegaResult} actions={actions} />
        </div>
      )}
      <VegaEmbed
        spec={{ ...chart, width: "container" } as TopLevelSpec}
        className="w-full"
        options={{
          mode: "vega-lite",
          actions: false,
          // Evaluate spec expressions via the AST interpreter instead of `new
          // Function`, so the CSP can drop 'unsafe-eval'.
          ast: true,
          expr: expressionInterpreter,
          config: {
            title: {
              anchor: "middle",
              font,
              fontSize: 16,
              fontWeight: 600,
              offset: 10,
            },
            axis: {
              labelFont: font,
              labelFontSize: 14,
              labelFontWeight: 400,
              titleFont: font,
              titleFontSize: 14,
              titleFontWeight: 500,
              titlePadding: 10,
            },
          },
        }}
        onEmbed={setVegaResult}
      />
    </div>
  );
};

const hasChartData = (content: AssistantMessageContent): content is ChartData =>
  content.message_type === MessageType.CHART &&
  "chart" in content &&
  Boolean(content.chart);

const getVegaSpecError = (spec: TopLevelSpec): string | null => {
  try {
    compile(spec);

    return null;
  } catch (err) {
    return err instanceof Error ? err.message : String(err);
  }
};

interface ValidatedChartProps {
  chart: TopLevelSpec;
  isComplete: boolean;
}

const ValidatedChart = ({ chart, isComplete }: ValidatedChartProps) => {
  const error = useMemo(() => getVegaSpecError(chart), [chart]);

  // A spec that won't compile is expected mid-stream, before mark/encoding
  // have arrived; only a failure on the finished message is a real problem
  // worth surfacing. Logging from an effect (rather than inline in render)
  // keeps it to once per broken spec instead of once per re-render.
  useEffect(() => {
    if (error && isComplete) {
      console.error("Vega spec validation error:", error);
    }
  }, [error, isComplete]);

  if (error) return null;

  return <VegaChartContainer chart={chart} />;
};

export const ChartRenderer: Renderer<AssistantMessageContent> = {
  type: "chart",

  canRender: (content: AssistantMessageContent): boolean =>
    hasChartData(content),

  render: (
    content: AssistantMessageContent,
    context?: RendererContext
  ): ReactNode => {
    if (!hasChartData(content)) return null;

    return (
      <ValidatedChart
        chart={content.chart}
        isComplete={Boolean(context?.message.endedAt)}
      />
    );
  },
};
